import {
  MeliPublicationTarget,
  PropertyPortal,
  type PropertyPortalPublication,
} from 'src/api/generated';
import type { I18nContextValue } from 'src/modules/shared/types/i18n/i18n.type';
import type { PropertyPublicationActionItem } from '../types/property-publication-actions.type';
import { getPublicationPortalTarget } from './property-meli-publishing-manager.util';

const openExternalPublication = (url: string): void => {
  if (typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Build public-link actions from the provider permalink and confirmed destinations.
 *
 * Portal Inmobiliario accepts a numeric Chilean item ID without a title slug.
 * Unknown destinations and IDs never imply that a Portal listing exists.
 *
 * @param publication - Publication including its saved destination and listing ID.
 * @param t - Translator for accessible action names and provider labels.
 * @returns Independent public-link actions, without changing the publication.
 */
export const buildPropertyPublicationExternalActions = (
  publication: PropertyPortalPublication,
  t: I18nContextValue['t']
): PropertyPublicationActionItem[] => {
  const actions: PropertyPublicationActionItem[] = [];
  const isMeli = publication.portal === PropertyPortal.MELI;
  const externalUrl = publication.externalUrl;
  const listingId =
    isMeli &&
    getPublicationPortalTarget(publication) === MeliPublicationTarget.MELI_POI
      ? publication.externalListingId?.trim().match(/^MLC-?(\d+)$/)
      : null;
  const portalUrl = listingId
    ? `https://www.portalinmobiliario.com/MLC-${listingId[1]}`
    : null;

  if (typeof externalUrl === 'string' && externalUrl.trim().length > 0) {
    const displayLabel = portalUrl
      ? t('catalog.properties.editor.publishing.target.meli')
      : undefined;
    const label = displayLabel
      ? t('catalog.properties.editor.publishing.actions.openInProvider', {
          provider: displayLabel,
        })
      : t('catalog.properties.editor.publishing.actions.openExternal');

    actions.push({
      key: 'open_external',
      kind: 'open_external',
      displayLabel,
      label,
      tooltip: displayLabel
        ? label
        : t('catalog.properties.editor.publishing.actions.openExternalTooltip'),
      onClick: () => openExternalPublication(externalUrl),
    });
  }

  if (portalUrl) {
    const displayLabel = 'Portal Inmobiliario';
    const label = t(
      'catalog.properties.editor.publishing.actions.openInProvider',
      {
        provider: displayLabel,
      }
    );

    actions.push({
      key: 'open_portal_inmobiliario',
      kind: 'open_external',
      displayLabel,
      label,
      tooltip: label,
      onClick: () => openExternalPublication(portalUrl),
    });
  }

  return actions;
};
